import { useEffect, useState } from 'react';
import { Select } from './ui';
import { api } from '../lib/api';
import { LINE_CODE_GROUPS, isPresetLineCode, isValidLineCode, lineCodeLabel, effectiveLineCode } from '../lib/fpaLineCodes';
import { useCatalogLabels, invalidateCatalogLabels } from '../hooks/useCatalogLabels';

// Inline account → FP&A-line picker, shared by the Accounts tab, the mapping
// coverage punch-list, and the Actuals "Unmapped" quick-map. Saves on change
// (preset) or on blur (custom code); shows the classification fallback as the
// placeholder so an unmapped account still reads as where it resolves today.

const CUSTOM = '__custom__';
const inputStyle: React.CSSProperties = {
  fontSize: 11, padding: '3px 6px', border: '0.5px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)',
};

export function LineCodeMapSelect({ qboAccountId, lineCode, classification, disabled, onSaved }: {
  qboAccountId: string; lineCode: string | null; classification: string | null; disabled?: boolean; onSaved: () => void;
}) {
  // The hard-coded preset list has no entry for a code registered in the DB
  // catalog, so without this a mapped `revenue.var.grants` rendered as its own
  // code instead of "Grants received".
  const cat = useCatalogLabels();
  /**
   * The CATALOG wins. The preset list is a hard-coded fallback for a code the
   * catalog has not loaded or does not hold — consulting it first meant a line
   * somebody had renamed ("Time & Materials" → "Services Income — T&M") still
   * read as its original name in every mapping picker, which looks like the
   * rename silently failed.
   */
  const labelFor = (c: string | null) => {
    if (!c) return 'Unmapped';
    const fromCatalog = cat.labelOf(c);
    return fromCatalog !== c ? fromCatalog : lineCodeLabel(c);
  };
  /** 'Payroll Expenses › Sales Compensation › Salaries - Sales'. */
  const pathFor = (c: string) => [...cat.ancestorsOf(c), labelFor(c)].join(' › ');
  // A code the catalog knows is not "custom" any more - it can be picked.
  const known = (c: string) => isPresetLineCode(c) || cat.leaves.some((l) => l.code === c);
  const isCustom = (c: string | null) => c != null && !known(c);
  /**
   * "Is this a custom code?" cannot be answered until the catalog has loaded,
   * and the catalog arrives asynchronously from a module-level promise.
   *
   * THE BUG THIS FIXES. These were initialised from `isCustom(lineCode)` on the
   * first render — before the catalog resolved — so every catalog code looked
   * unknown and the row opened in "Custom code…" mode. The effect that would
   * have corrected it depended on `[lineCode]` alone, which never changes, so
   * the correction never ran: EVERY mapped account on the page sat in custom
   * mode showing its raw code, making a fully-mapped chart of accounts read as
   * though nothing was mapped at all.
   */
  const [custom, setCustom] = useState(false);
  const [customVal, setCustomVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    // Decide nothing until the catalog is in: guessing early is what broke it.
    if (!cat.loaded) return;
    setCustom(isCustom(lineCode));
    setCustomVal(isCustom(lineCode) ? lineCode! : '');
    setErr(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineCode, cat.loaded]);

  async function save(next: string | null) {
    setErr(null); setSaving(true);
    try { await api.fpa.setLineMap(qboAccountId, next); invalidateCatalogLabels(); onSaved(); }
    catch (e) { setErr((e as Error).message); }
    finally { setSaving(false); }
  }

  // Until the catalog is in, show the mapping as text rather than a control
  // whose options do not exist yet — a select with no matching option renders
  // as whatever happens to be first, which reads as a wrong answer.
  if (!cat.loaded) {
    return lineCode
      ? <span style={{ fontSize: 11 }} title={lineCode}>{labelFor(lineCode)}</span>
      : <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Unmapped</span>;
  }

  if (disabled) {
    // The path on hover: a bare label is ambiguous once a mirrored chart has
    // put the same name in several branches.
    return lineCode
      ? <span style={{ fontSize: 11 }} title={`${pathFor(lineCode)}
${lineCode}`}>{labelFor(lineCode)}</span>
      : <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Unmapped</span>;
  }

  function onSelect(v: string) {
    if (v === CUSTOM) { setCustom(true); return; }
    setCustom(false);
    save(v === '' ? null : v);
  }
  function saveCustom() {
    const next = customVal.trim();
    if (next === '') { save(null); return; }
    if (!isValidLineCode(next)) { setErr('Use rev.*, cost.salary, cost.var.* or cost.fixed.*'); return; }
    save(next);
  }

  const fallback = effectiveLineCode(null, classification);
  const selectValue = custom ? CUSTOM : (lineCode ?? '');

  // Mappable leaves, grouped by their top-level branch and labelled with the
  // path below it, so two leaves with the same name are told apart.
  const catalogGroups = (() => {
    const byRoot = new Map<string, Array<{ code: string; label: string; sort: string }>>();
    for (const l of cat.leaves) {
      const ancestors = cat.ancestorsOf(l.code);
      const root = ancestors[0] ?? labelFor(l.code);
      const below = [...ancestors.slice(1), labelFor(l.code)].join(' › ');
      const list = byRoot.get(root) ?? byRoot.set(root, []).get(root)!;
      list.push({ code: l.code, label: below || labelFor(l.code), sort: below });
    }
    return [...byRoot.entries()]
      .map(([root, options]) => ({ root, options: options.sort((a, b) => a.sort.localeCompare(b.sort)) }));
  })();

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <Select value={selectValue} onChange={onSelect}
        title={lineCode ? `${pathFor(lineCode)}
${lineCode}` : undefined}>
        <option value="">— Unmapped{fallback ? ` → ${labelFor(fallback)}` : ''} —</option>
        {/*
          Driven by the catalog, grouped by top-level branch, each option
          showing its path below that branch.

          WHY NOT ONE FLAT LIST. Mirroring a real chart of accounts produces
          hundreds of leaves, and running the mirror twice with different shapes
          produces the same label three times over in different branches. A flat
          "Registered lines" group of 380 near-duplicate names is not a control
          anyone can use — picking the right "Salaries - Sales" was guesswork.
          The hard-coded preset groups are only a fallback for a catalog that
          has not loaded, or codes it does not hold.
        */}
        {catalogGroups.length > 0
          ? catalogGroups.map((g) => (
            <optgroup key={g.root} label={g.root}>
              {g.options.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
            </optgroup>
          ))
          : LINE_CODE_GROUPS.map((g) => (
            <optgroup key={g.group} label={g.group}>
              {g.options.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
            </optgroup>
          ))}
        {/* A code the catalog does not hold, so it is not in any group above.
            Kept selectable, or changing any OTHER field on the row would
            silently re-point this account. */}
        {lineCode && !known(lineCode) && !custom && (
          <optgroup label="Not in the catalog">
            <option value={lineCode}>{lineCode}</option>
          </optgroup>
        )}
        <option value={CUSTOM}>Custom code…</option>
      </Select>
      {custom && (
        <input
          autoFocus value={customVal} placeholder="e.g. cost.fixed.insurance"
          onChange={(e) => setCustomVal(e.target.value)}
          onBlur={saveCustom}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          style={{ ...inputStyle, width: 180 }}
        />
      )}
      {saving && <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>saving…</span>}
      {err && <span style={{ fontSize: 10, color: 'var(--color-red-text)' }}>{err}</span>}
    </span>
  );
}

/** Account → department override picker. "(by class)" = no override; the class
 *  dimension decides. Use for department-specific accounts (e.g. Salaries – Eng). */
export function AccountDepartmentSelect({ qboAccountId, departmentId, departments, disabled, onSaved }: {
  qboAccountId: string; departmentId: string | null; departments: Array<{ id: string; name: string }>; disabled?: boolean; onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  if (disabled) {
    const name = departments.find((d) => d.id === departmentId)?.name;
    return <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{name ?? (departmentId ?? 'by class')}</span>;
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Select value={departmentId ?? ''} onChange={async (v) => { setSaving(true); try { await api.fpa.setAccountDepartment(qboAccountId, v || null); onSaved(); } finally { setSaving(false); } }}>
        <option value="">(by class)</option>
        {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
      </Select>
      {saving && <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>saving…</span>}
    </span>
  );
}
